import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Headline, Gutter, Div, Button, Flex, BodyText, List, ModalDialog, Icon } from '@gaiads/telia-react-component-library';

import { getUserGroups as getAllUserGroups, showGrowlNotification } from '../../../../store/actions';
import { RootState } from '../../../../store/reducers';
import { downloadUsersTemplateSpreadsheet, importUsersSpreadsheet, ExcelUserImportErrorType } from '../../../../utils/spreadsheetUtils';
import { IMPORT_USERS_FLOW_CONSTANTS } from '../../../../utils/constants';

import { User } from '../../../../types';
import { UploadErrorNotification } from './UploadErrorNotification/UploadErrorNotification';

type UploadTemplateProps = {
  setUsersToImport: (users: User[]) => void;
};

const UploadTemplate: FunctionComponent<UploadTemplateProps> = ({ setUsersToImport }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [usersGottenFromUploadedFile, setUsersGottenFromUploadedFile] = useState<boolean>(false);
  const [errorGettingUsersFromUploadedFile, setErrorGettingUsersFromUploadedFile] = useState<ExcelUserImportErrorType>();

  const userGroups = useSelector((state: RootState) => state.userGroups.userGroups);

  const { t } = useTranslation('editOrganization');

  useEffect(() => {
    dispatch(getAllUserGroups());
  }, [dispatch]);

  const resetUploadedFileData = () => {
    setErrorGettingUsersFromUploadedFile(undefined);
    setUploadedFile(undefined);
    setUsersToImport([]);
    setUsersGottenFromUploadedFile(false);
  };

  const checkExcelTemplateCanBeGenerated = () => {
    const userGroupNames = userGroups.map(userGroup => userGroup.name);

    const findDuplicates = (arr: string[]) => {
      const existing: string[] = [];
      const duplicates = new Set<string>();
      arr.forEach(item => {
        if (existing.includes(item)) {
          duplicates.add(item);
        } else {
          existing.push(item);
        }
      });
      return duplicates;
    };

    const duplicateUserGroupNames = findDuplicates(userGroupNames);
    if (duplicateUserGroupNames.size) {
      dispatch(
        showGrowlNotification(
          t(
            'usersAdmin.importUsers.uploadTemplate.downloadTemplateErrorNotification',
            { userGroupNames: Array.from(duplicateUserGroupNames).join(', '), interpolation: { escapeValue: false } }, // React should handle escaping
          ),
          'error',
        ),
      );
      return false;
    }
    return true;
  };

  const customFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    return false;
  };

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    const uploadedFile = files?.[0];
    if (uploadedFile) {
      resetUploadedFileData();
      setUploadedFile(uploadedFile);
      importUsersSpreadsheet(
        uploadedFile,
        userGroups,
        users => {
          setUsersToImport(users);
          setUsersGottenFromUploadedFile(true);
        },
        (errorType: ExcelUserImportErrorType) => {
          setErrorGettingUsersFromUploadedFile(errorType);
        },
      );
    }
    event.target.value = '';
  };

  return (
    <Div>
      <Headline size="md">{t('usersAdmin.importUsers.uploadTemplate.title')}</Headline>
      <Gutter size={'layout-7'} />
      <BodyText fontWeight={'medium'}>{t('usersAdmin.importUsers.uploadTemplate.mainInfo')}</BodyText>
      <Gutter size={'sm'} />
      <List numbered={true} padding={{ size: 'xxxs' }}>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forUserGroup')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forExpenseSharing')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forDownload')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forFill')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forSave')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList.forUpload')}</List.Item>
      </List>
      <Gutter size={'lg'} />
      <BodyText fontWeight={'medium'}>{t('usersAdmin.importUsers.uploadTemplate.infoList2.title')}</BodyText>
      <Gutter size={'sm'} />
      <List disc padding={{ size: 'xxxs' }}>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forStartRow')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forSheet')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forOnboardingDate')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forUniqueness')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forEmailAccess')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forPhoneNumberFormat')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forCountryCallingCode')}</List.Item>
        <List.Item>{t('usersAdmin.importUsers.uploadTemplate.infoList2.forEmployeeId')}</List.Item>
      </List>
      <Gutter size={'xxlg'} />
      <Flex>
        <Button onClick={() => setUploadModalOpen(true)} disabled={!userGroups.length} variant={'primary'} iconName={'upload'} iconOnRight={true}>
          {t('usersAdmin.importUsers.uploadTemplate.uploadFile')}
        </Button>
        <Gutter size={'sm'} />
        <Button
          onClick={() => {
            if (checkExcelTemplateCanBeGenerated()) {
              downloadUsersTemplateSpreadsheet(userGroups);
            }
          }}
          disabled={!userGroups.length}
          variant={'secondary'}
          iconName={'download'}
          iconOnRight={true}
        >
          {t('usersAdmin.importUsers.uploadTemplate.downloadTemplate')}
        </Button>
      </Flex>
      <Gutter size={'lg'} />

      <ModalDialog
        isOpen={uploadModalOpen}
        title={t('usersAdmin.importUsers.uploadTemplate.modalTitle')}
        onCloseClick={() => {
          resetUploadedFileData();
          setUploadModalOpen(false);
        }}
      >
        {/* Custom file uploader - telia FileUploader could not be made to match design, and it's onDelete callback of files did not seem to work */}
        <>
          <Button onClick={customFileUpload} disabled={!!uploadedFile} variant={'secondary'} iconName={'plus'} iconOnRight={true}>
            {t('usersAdmin.importUsers.uploadTemplate.chooseFile')}
          </Button>
          <input type="file" accept=".xlsx" onChange={onFileInputChange} hidden={true} ref={fileInputRef} data-testid={'file-input'} />
          <Gutter />
          {!uploadedFile && <BodyText color="gray500">{t('usersAdmin.importUsers.uploadTemplate.noFileSelected')}</BodyText>}
          {uploadedFile && (
            <>
              <Flex centeredVertically>
                <BodyText color="gray500">{t('usersAdmin.importUsers.uploadTemplate.selectedFile')}</BodyText>
                <Gutter size={'xlg'} />
                {usersGottenFromUploadedFile && <Icon name="check-circle-filled" size="sm" color="green700" />}
                <Gutter size={'xs'} />
                <BodyText>{uploadedFile.name}</BodyText>
                <Gutter size={'xlg'} />
                <Icon name="delete" size="md" color="purple500" style={{ cursor: 'pointer' }} onClick={() => resetUploadedFileData()} />
              </Flex>
              {errorGettingUsersFromUploadedFile && (
                <UploadErrorNotification
                  errorGettingUsersFromUploadedFile={errorGettingUsersFromUploadedFile}
                  resetUploadedFileData={resetUploadedFileData}
                  t={t}
                />
              )}
            </>
          )}
        </>

        <Gutter />
        <Button
          onClick={() => history.push(IMPORT_USERS_FLOW_CONSTANTS.reviewAndSave)}
          disabled={!usersGottenFromUploadedFile}
          variant={'primary'}
          iconName={'download'}
          iconOnRight={true}
        >
          {t('usersAdmin.importUsers.uploadTemplate.uploadSelectedFile')}
        </Button>
      </ModalDialog>
    </Div>
  );
};

export default UploadTemplate;
